
const API_BASE_URL = 'http://tse-consultant-int-app.runasp.net/api';

/**
 * Crée une nouvelle commande avec ses transactions (VERSION V2 - AVEC EMAIL)
 * @param {Object} commandeData - Données de la commande (CommandeCreateDTO)
 * @param {Array} transactions - Liste des transactions (TransactionCreateDto[])
 * @param {string} emailClient - Email du client
 * @param {string} paymentMethod - Méthode de paiement
 * @param {string} token - Token d'authentification (optionnel)
 * @returns {Promise<Object>} Résultat de la création
 */
export const createCommandeV2 = async (commandeData, transactions, emailClient, paymentMethod, token = null) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const requestBody = {
      Commande: commandeData,
      Transactions: transactions,
      EmailClient: emailClient,       
      PaymentMethod: paymentMethod      
    };

    console.log('📤 Envoi de la commande V2:', JSON.stringify(requestBody, null, 2));

    const response = await fetch(`${API_BASE_URL}/Commande/Create-Commande-V2`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    const responseData = await response.json().catch(() => null);

    console.log('📥 Réponse du serveur:', responseData);

    if (!response.ok) {
      console.error('❌ Erreur HTTP:', response.status, responseData);
      throw new Error(responseData?.message || `Erreur HTTP: ${response.status}`);
    }

    return {
      success: true,
      message: responseData?.message || 'Commande créée avec succès',
      data: responseData
    };

  } catch (error) {
    console.error('❌ Erreur lors de la création de la commande:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la création de la commande'
    };
  }
};


/**
 * Crée une nouvelle commande avec ses transactions (VERSION ORIGINALE)
 * Conservée pour compatibilité avec l'ancien endpoint
 * @param {Object} commandeData - Données de la commande (CommandeCreateDTO)
 * @param {Array} transactions - Liste des transactions (TransactionCreateDto[])
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Résultat de la création
 */
export const createCommande = async (commandeData, transactions, token) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const transactionsQuery = transactions.map((tr, index) =>
      `ListTransactions=${encodeURIComponent(JSON.stringify(tr))}`
    ).join('&');

    const url = `${API_BASE_URL}/Commande/Create-Commande?${transactionsQuery}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(commandeData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return {
      success: true,
      message: 'Commande créée avec succès'
    };

  } catch (error) {
    console.error('Erreur lors de la création de la commande:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la création de la commande'
    };
  }
};

/**
 * Récupère les commandes de l'utilisateur connecté
 * @param {string} token - Token d'authentification
 * @returns {Promise<Array>} Liste des commandes
 */
export const getUserCommandes = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Commande/Get-List-Commandes`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération des commandes:', error);
    throw error;
  }
};

/**
 * Récupère une commande par son ID
 * @param {number} commandeId - ID de la commande
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Détails de la commande
 */
export const getCommandeById = async (commandeId, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Commande/get-Commande-ByID/${commandeId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération de la commande:', error);
    throw error;
  }
};

/**
 * Récupère une commande par sa référence
 * @param {string} reference - Référence de la commande
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Détails de la commande
 */
export const getCommandeByRef = async (reference, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Commande/get-Commande-ByRef/${reference}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Erreur lors de la récupération de la commande:', error);
    throw error;
  }
};

/**
 * Récupère le total d'une commande
 * @param {number} commandeId - ID de la commande
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Montants HT et TTC
 */
export const getTotalCommande = async (commandeId, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Commande/get-Total-Commande-ByID/${commandeId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    return {
      totalHT: data.item1 || data[0],
      totalTTC: data.item2 || data[1]
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du total:', error);
    throw error;
  }
};

/**
 * Met à jour une commande existante
 * @param {number} commandeId - ID de la commande à mettre à jour
 * @param {Object} commandeData - Nouvelles données de la commande
 * @param {Array} transactions - Nouvelles transactions
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Résultat de la mise à jour
 */
export const updateCommande = async (commandeId, commandeData, transactions, token) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    const transactionsQuery = transactions.map((tr) =>
      `ListTransactions=${encodeURIComponent(JSON.stringify(tr))}`
    ).join('&');

    const url = `${API_BASE_URL}/Commande/update-Commande/${commandeId}?${transactionsQuery}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(commandeData)
    });

    const responseData = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(responseData?.message || `Erreur HTTP: ${response.status}`);
    }

    return {
      success: true,
      message: responseData?.message || 'Commande mise à jour avec succès'
    };

  } catch (error) {
    console.error('Erreur lors de la mise à jour de la commande:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la mise à jour de la commande'
    };
  }
};

/**
 * Supprime une commande
 * @param {number} commandeId - ID de la commande à supprimer
 * @param {string} token - Token d'authentification
 * @returns {Promise<Object>} Résultat de la suppression
 */
export const deleteCommande = async (commandeId, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Commande/delete-Commande/${commandeId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.item2 || data?.message || `Erreur HTTP: ${response.status}`);
    }

    return {
      success: data?.item1 || true,
      message: data?.item2 || data?.message || 'Commande supprimée avec succès'
    };

  } catch (error) {
    console.error('Erreur lors de la suppression de la commande:', error);
    return {
      success: false,
      message: error.message || 'Erreur lors de la suppression de la commande'
    };
  }
};

const commandeService = {
  createCommande: createCommandeV2, 
  createCommandeV2,
  createCommandeLegacy: createCommande, 
  getUserCommandes,
  getCommandeById,
  getCommandeByRef,
  getTotalCommande,
  updateCommande,
  deleteCommande
};

export default commandeService;